import type { DataActionsType } from '@aytm/app/insights/dashboard/duckness/ducks/DataDuck/DataDuck.actions'
import type { DataDuckSelectorsType } from '@aytm/app/insights/dashboard/duckness/ducks/DataDuck/DataDuck.types'
import type { InsightsDashboardPoolState } from '@aytm/app/insights/dashboard/duckness/ducks/types'
import SagaDuck from '@duckness/saga'

import { defaultEditor } from '@aytm/app/insights/dashboard/lib/config'
import {
  updateState,
  updateCurrentBlock,
  updateCurrentData,
  updateRequestQueue,
  updateRequestChain,
  updateSplitOptions,
  getDataItemsProgress,
  getStatItemsProgress,
  getCurrentBlock,
  updateLoading
} from '@aytm/app/insights/dashboard/helpers/requests'
import { processItems, updateHiddenItems } from '@aytm/app/insights/dashboard/helpers/items'
import { updateEditor } from '@aytm/app/insights/dashboard/helpers/editor'
import { getPortalFilters, getPanelFilters } from '@aytm/app/insights/dashboard/helpers/filters'
import { updateTags } from '@aytm/app/insights/dashboard/helpers/tags'
import { addOrUpdateSubject, type UpdateAction } from '@aytm/app/insights/dashboard/helpers/subjects'

import { DATA_ACTIONS } from './DataDuck.actions'
import {
  schemaHandlers,
  dataHashesHandlers,
  dataItemsHandlers,
  statHashesHandlers,
  statItemsHandlers,
  type PositiveServerResponse
} from './DataDuck.handlers'
import { duckName } from './constants'
import type { ServerResponse } from '@aytm/app/insights/dashboard/models/types.ServerResponse'
import type { SubjectType } from '@aytm/app/insights/dashboard/models/types.ClientDTO'
import { withStateUpdates } from '@aytm/app/insights/dashboard/duckness/ducks/withStateUpdates'

export const DataDuck = SagaDuck<InsightsDashboardPoolState, DataActionsType, DataDuckSelectorsType>(
  duckName,
  'insights-dashboard'
)

// actions
DataDuck.action(DATA_ACTIONS.ALLOW_REQUEST_SCHEMA)
DataDuck.action(DATA_ACTIONS.SKIP_RESPONSE)
DataDuck.action(DATA_ACTIONS.GET_SCHEMA)
DataDuck.action(DATA_ACTIONS.GET_DATA_HASHES)
DataDuck.action(DATA_ACTIONS.GET_DATA_ITEMS)
DataDuck.action(DATA_ACTIONS.GET_STAT_HASHES)
DataDuck.action(DATA_ACTIONS.GET_STAT_ITEMS)
DataDuck.action(DATA_ACTIONS.FETCH_SCHEMA)
DataDuck.action(DATA_ACTIONS.FETCH_DATA_HASHES)
DataDuck.action(DATA_ACTIONS.FETCH_DATA_ITEMS)
DataDuck.action(DATA_ACTIONS.FETCH_STAT_HASHES)
DataDuck.action(DATA_ACTIONS.FETCH_STAT_ITEMS)
DataDuck.action(DATA_ACTIONS.SET_SCHEMA)
DataDuck.action(DATA_ACTIONS.SET_DATA_HASHES)
DataDuck.action(DATA_ACTIONS.SET_DATA_ITEMS)
DataDuck.action(DATA_ACTIONS.SET_STAT_HASHES)
DataDuck.action(DATA_ACTIONS.SET_STAT_ITEMS)
DataDuck.action(DATA_ACTIONS.WAIT_SCHEMA)
DataDuck.action(DATA_ACTIONS.WAIT_DATA_HASHES)
DataDuck.action(DATA_ACTIONS.WAIT_STAT_HASHES)
DataDuck.action(DATA_ACTIONS.END_REQUEST_CHAIN)
DataDuck.action(DATA_ACTIONS.SUBJECT_UPDATE)
DataDuck.action(DATA_ACTIONS.SUBJECT_UPDATE_SET)
DataDuck.action(DATA_ACTIONS.TAGS_UPDATE)
DataDuck.action(DATA_ACTIONS.TAGS_UPDATE_SET)

// selectors

DataDuck.selector('currentBlockUID', state => state.currentSchema?.get('currentBlockUID'))
DataDuck.selector('blocks', state => state.currentSchema?.get('blocks'))
DataDuck.selector('accessKey', state => state.accessKey)
DataDuck.selector('roles', state => state.roles)
DataDuck.selector('state', state => state)
DataDuck.selector('data', state => state.currentData?.data)
DataDuck.selector('stat', state => state.currentData?.stat)
DataDuck.selector('isFreshLoading', state => state.isFreshLoading)
DataDuck.selector('isLoading', state => !state.isFreshLoading && state.isLoading)
DataDuck.selector('loadingProgress', state => state.requestChain.progress)
DataDuck.selector('requestQueue', state => state.requestChain.queue)
DataDuck.selector('requestQueueIndex', state => state.requestChain.currentIndex)
DataDuck.selector('isPastRetrievingData', state =>
  state.requestChain.queue.some(item => 'dataItems' === item.key && 'completed' === item.status)
)
DataDuck.selector('isPastRetrievingStats', state =>
  state.requestChain.queue.some(item => 'statItems' === item.key && 'completed' === item.status)
)
DataDuck.selector('isLoadingTimestamp', state => state.isLoadingTimestamp ?? 0)
DataDuck.selector('showSigtest', state => {
  const block = getCurrentBlock(state)
  return block?.get('showSigtest') || false
})

// reducers

/**
 * Start GET_SCHEMA request.
 */
DataDuck.reducer<{ timestamp?: number }>(DATA_ACTIONS.GET_SCHEMA, (state, action) => {
  // Set waiting state
  let resultState = updateLoading(state, true, action.payload.timestamp)
  // Initialize current data/stat
  resultState = updateCurrentData(resultState, { stat: {} })
  // Update request queue on first run
  resultState = updateRequestQueue(resultState, { key: 'schema', status: 'progress' })
  resultState = updateRequestQueue(resultState, { key: 'dataHashes', status: 'pending' })
  resultState = updateRequestQueue(resultState, { key: 'dataItems', status: 'pending' })
  resultState = updateRequestQueue(resultState, { key: 'statHashes', status: 'pending' })
  resultState = updateRequestQueue(resultState, { key: 'statItems', status: 'pending' })
  // Return state
  return resultState
})

/**
 * Set the received schema in the state.
 */
DataDuck.reducer<{ response: PositiveServerResponse }>(DATA_ACTIONS.SET_SCHEMA, (state, action) => {
  // Get response from action payload
  const { response } = action.payload

  // Update current state from response
  let resultState = state
  for (const handler of schemaHandlers) {
    resultState = handler(response, resultState)
  }
  // Initialize current portal filters
  if (!resultState.currentData?.portalCurrentFilters) {
    const portalCurrentFilters = getPortalFilters(resultState)
    resultState = updateCurrentData(resultState, { portalCurrentFilters })
  }
  // Initialize current panel filters
  if (!resultState.currentData?.panelCurrentFilters) {
    const panelCurrentFilters = getPanelFilters(resultState)
    resultState = updateCurrentData(resultState, { panelCurrentFilters })
  }
  // Update split/wave options
  resultState = updateSplitOptions(resultState)
  // Update request queue
  resultState = updateRequestQueue(resultState, { key: 'schema', status: 'completed' })
  // Update items in the current block from response
  const items = processItems(resultState)
  resultState = updateCurrentBlock(resultState, { items })

  return resultState
})

/**
 * Perform actions on GET_SCHEMA wait response status.
 */
DataDuck.reducer<{ response: ServerResponse.WaitServerResponse }>(DATA_ACTIONS.WAIT_SCHEMA, (state, action) => {
  const { progress } = action.payload?.response || {}
  return updateRequestChain(state, { progress: progress || 0 })
})

/**
 * Start GET_DATA_HASHES request.
 */
DataDuck.reducer<{ timestamp?: number }>(DATA_ACTIONS.GET_DATA_HASHES, (state, action) => {
  // Set waiting state
  let resultState = updateLoading(state, true, action.payload?.timestamp)
  // Update request queue on first run
  resultState = updateRequestQueue(resultState, { key: 'dataHashes', status: 'progress' })
  resultState = updateRequestQueue(resultState, { key: 'dataItems', status: 'pending' })
  resultState = updateRequestQueue(resultState, { key: 'statHashes', status: 'pending' })
  resultState = updateRequestQueue(resultState, { key: 'statItems', status: 'pending' })
  return resultState
})

/**
 * Set the received data hashes in the state.
 */
DataDuck.reducer<{ response: Extract<ServerResponse.InsightsDataHashes.RootType, { status: 'OK' }> }>(
  DATA_ACTIONS.SET_DATA_HASHES,
  (state, action) => {
    let resultState = state
    const { response } = action.payload
    for (const handler of dataHashesHandlers) {
      resultState = handler(response, resultState)
    }
    // Update request queue
    resultState = updateRequestQueue(resultState, { key: 'dataHashes', status: 'completed' })
    return resultState
  }
)

/**
 * Perform actions on WAIT_DATA_HASHES response status.
 */
DataDuck.reducer<{ response: ServerResponse.WaitServerResponse }>(DATA_ACTIONS.WAIT_DATA_HASHES, (state, action) => {
  const { progress } = action.payload?.response || {}
  return updateRequestChain(state, { progress: progress || 0 })
})

/**
 * Start GET_DATA_ITEMS request.
 */
DataDuck.reducer(DATA_ACTIONS.GET_DATA_ITEMS, state =>
  withStateUpdates(state)(
    // Fresh loading flag
    state => updateState(state, { isFreshLoading: false }),
    // Update request queue on first run
    state => updateRequestQueue(state, { key: 'dataItems', status: 'progress' }),
    state => updateRequestQueue(state, { key: 'statHashes', status: 'pending' })
  )
)

/**
 * Set the received data items in the state.
 */
DataDuck.reducer<{ response: Extract<ServerResponse.InsightsDataItems.RootType, { status: 'OK' }>; hashes: string[] }>(
  DATA_ACTIONS.SET_DATA_ITEMS,
  (state, action) => {
    const { response, hashes } = action.payload
    const processingStateUpdates = dataItemsHandlers.map(
      handler => (state: InsightsDashboardPoolState) => handler({ ...response, hashes }, state)
    )
    return withStateUpdates(state)(
      ...processingStateUpdates,
      state => {
        // Update request chain progress
        const progress = getDataItemsProgress(state)
        let resultState = updateRequestChain(state, { progress })
        if (100 === progress) {
          // Update request queue on complete
          resultState = updateRequestQueue(resultState, { key: 'dataItems', status: 'completed' })
        }
        return resultState
      },
      // Update hidden items array
      state => updateHiddenItems(state)
    )
  }
)

/**
 * Start GET_STAT_HASHES request.
 */
DataDuck.reducer<{ timestamp?: number }>(DATA_ACTIONS.GET_STAT_HASHES, (state, action) => {
  // Show sigtest flag
  const block = getCurrentBlock(state)
  const showSigtest = block?.get<boolean>('showSigtest') || false
  // Set waiting state
  let resultState = updateLoading(state, showSigtest, action.payload?.timestamp)
  // Update request queue on first run
  resultState = updateRequestQueue(resultState, { key: 'statHashes', status: 'progress' })
  resultState = updateRequestQueue(resultState, { key: 'statItems', status: 'pending' })
  return resultState
})

/**
 * Set the received stat hashes in the state.
 */
DataDuck.reducer<{ response: Extract<ServerResponse.InsightsStatHashes.RootType, { status: 'OK' }> }>(
  DATA_ACTIONS.SET_STAT_HASHES,
  (state, action) => {
    let resultState = state
    const { response } = action.payload
    for (const handler of statHashesHandlers) {
      resultState = handler(response, resultState)
    }
    // Update request queue
    resultState = updateRequestQueue(resultState, { key: 'statHashes', status: 'completed' })
    return resultState
  }
)

/**
 * Perform actions on WAIT_STAT_HASHES response status.
 */
DataDuck.reducer<{ response: ServerResponse.WaitServerResponse }>(DATA_ACTIONS.WAIT_STAT_HASHES, (state, action) => {
  const { progress } = action.payload?.response || {}
  return updateRequestChain(state, { progress: progress || 0 })
})

/**
 * Start get stat items request.
 */
DataDuck.reducer<{ timestamp?: number }>(DATA_ACTIONS.GET_STAT_ITEMS, (state, action) => {
  // Show sigtest flag
  const block = getCurrentBlock(state)
  const showSigtest: boolean = block?.get('showSigtest') || false
  // Set waiting state
  let resultState = updateLoading(state, showSigtest, action.payload?.timestamp)
  // Update request queue on first run
  resultState = updateRequestQueue(resultState, { key: 'statItems', status: 'progress' })
  return resultState
})

/**
 * Set the received stat items in the state.
 */
DataDuck.reducer<{ response: Extract<ServerResponse.InsightsStatItems.RootType, { status: 'OK' }>; hashes: string[] }>(
  DATA_ACTIONS.SET_STAT_ITEMS,
  (state, action) => {
    let resultState = state
    const { response, hashes } = action.payload
    for (const handler of statItemsHandlers) {
      resultState = handler({ ...response, hashes }, resultState)
    }
    // Update request chain progress
    const progress = getStatItemsProgress(resultState)
    resultState = updateRequestChain(resultState, { progress })
    if (100 === progress) {
      // Update request queue on complete
      resultState = updateRequestQueue(resultState, { key: 'statItems', status: 'completed' })
    }
    return resultState
  }
)

/**
 * Complete request chain.
 */
DataDuck.reducer(DATA_ACTIONS.END_REQUEST_CHAIN, state => {
  // Restore loading state
  let resultState = updateLoading(state, false)
  // Update request chain
  resultState = updateRequestChain(resultState, { currentIndex: -1 })
  return resultState
})

/**
 * Subject update actions.
 */
DataDuck.reducer(DATA_ACTIONS.SUBJECT_UPDATE, state => updateEditor(state, { state: 'updating' }))

/**
 * Subject update set action.
 */
DataDuck.reducer<{ data: SubjectType; action: UpdateAction }>(DATA_ACTIONS.SUBJECT_UPDATE_SET, (state, action) => {
  let resultState = updateState(state, { editor: defaultEditor })
  const { data, action: updateAction } = action.payload || {}
  if (data?.uid) {
    resultState = addOrUpdateSubject(resultState, data, updateAction)
  }
  return resultState
})

DataDuck.reducer<{ uid: string; tags: string[]; deleted?: string }>(DATA_ACTIONS.TAGS_UPDATE_SET, (state, action) =>
  updateTags(state, action.payload)
)
